/**
 * extractFieldsFromText — Simple keyword-based NLP extraction
 *
 * Mirrors the backend createActivityFlow logic for client-side fast extraction.
 * Used as a fallback / instant preview before the agent API responds.
 *
 * Extracts: category, date, maxParticipants from free-text input.
 *
 * @param {string} text - User free-text input
 * @param {Array} categories - Categories from useCategories() hook
 * @returns {Object} Extracted fields (only set fields are returned)
 */

const CATEGORY_KEYWORDS = {
  sport: ['foot', 'tennis', 'course', 'yoga', 'natation', 'vélo', 'rando', 'randonnée', 'match', 'gym', 'sport', 'basket', 'volley', 'rugby', 'surf', 'ski', 'boxe', 'escalade', 'balade'],
  outing: ['bar', 'soirée', 'concert', 'apéro', 'fête', 'festival', 'sortie', 'pique-nique', 'picnic'],
  cooking: ['cuisine', 'repas', 'dîner', 'déjeuner', 'restaurant', 'brunch', 'barbecue', 'bbq'],
  culture: ['ciné', 'cinéma', 'théâtre', 'musée', 'expo', 'exposition', 'spectacle', 'lecture', 'livre'],
  meeting: ['café', 'networking', 'rencontre', 'discussion', 'afterwork'],
  leisure: ['jeux', 'jeu', 'escape', 'bowling', 'karting', 'arcade'],
  kids: ['enfant', 'famille', 'familial', 'parc', 'goûter', 'atelier créatif'],
  transport: ['covoiturage', 'covoit', 'trajet', 'voiture'],
  pets: ['chien', 'chat', 'animal', 'canin', 'promenade'],
};

/** Try to extract a date from natural French language.
 *  Returns only the raw ISO date — localized labels are computed by the component. */
const extractDate = (lower) => {
  const now = new Date();

  if (lower.includes('ce soir')) {
    const d = new Date(now);
    d.setHours(19, 0, 0, 0);
    if (d > now) return d.toISOString();
  }

  if (lower.includes('demain soir')) {
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    d.setHours(19, 0, 0, 0);
    return d.toISOString();
  }

  if (lower.includes('demain')) {
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    d.setHours(12, 0, 0, 0);
    return d.toISOString();
  }

  if (lower.includes('samedi')) {
    const days = (6 - now.getDay() + 7) % 7 || 7;
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    d.setHours(10, 0, 0, 0);
    return d.toISOString();
  }

  if (lower.includes('dimanche')) {
    const days = (7 - now.getDay() + 7) % 7 || 7;
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    d.setHours(10, 0, 0, 0);
    return d.toISOString();
  }

  if (lower.includes('weekend') || lower.includes('week-end')) {
    const days = (6 - now.getDay() + 7) % 7 || 7;
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    d.setHours(10, 0, 0, 0);
    return d.toISOString();
  }

  return null;
};

export const extractFieldsFromText = (text, categories = []) => {
  const lower = text.toLowerCase();
  const result = {};

  // Category detection — returns only raw id + icon (labels are locale-dependent,
  // computed by the component via categories data + i18n).
  for (const [catId, words] of Object.entries(CATEGORY_KEYWORDS)) {
    if (words.some((w) => lower.includes(w))) {
      const cat = categories.find((c) => c.id === catId);
      if (cat) {
        result.category = catId;
        result.categoryIcon = cat.icon;
      }
      break;
    }
  }

  // Date detection — returns only ISO date string (label is locale-dependent,
  // formatted by the component via Intl.DateTimeFormat).
  const dateResult = extractDate(lower);
  if (dateResult) {
    result.date = dateResult;
  }

  // Participants detection
  const pMatch = lower.match(/(\d+)\s*(personnes?|pers|max|places?|participants?)/);
  if (pMatch) {
    result.maxParticipants = pMatch[1];
  }

  return result;
};
